import { AxiosResponse } from 'axios';
import { notify } from 'front-commons/ds';
import { Typography } from 'hype-ds';
import { getSignupOTP, getVerificationOTP, validateSignupOPT, validateVerificationOTP } from '.';
import {
	getPartnerSignupOTP,
	getPartnerVerificationOTP,
	validatePartnerSignupOTP,
	validatePartnerVerificationOTP,
} from './partner';


const MAX_ATTEMPTS_WARNING_THRESHOLD = 3;
const BLOCK_DURATION_MINUTES = 15;
const NOTIFY_OFFSET_TOP = {
	medium: '98px 0 0',
	large: '72px 0 0',
};

export function loginRateLimitHandler(response: AxiosResponse<any, any>) {
	if (response.status === 401) {
		const { 'x-login-attempts-limit': maxAttempts, 'x-login-attempts': attempt } = response.headers;

		if (maxAttempts === attempt) {
			notify.negative({
				title: 'Excesso de tentativas',
				description: (
					<>
						Por motivo de segurança sua conta ficará bloqueada por{' '}
						<Typography.Label.M>{BLOCK_DURATION_MINUTES} minutos</Typography.Label.M>. Acesse com outra conta ou aguarde
						e tente novamente.
					</>
				),
				containerProps: { margin: NOTIFY_OFFSET_TOP },
			});

			return { cause: 'blocked' };
		}

		if (maxAttempts - attempt <= MAX_ATTEMPTS_WARNING_THRESHOLD) {
			const remainingAttempts = maxAttempts - attempt;

			const tries = remainingAttempts > 1 ? 'tentativas' : 'tentativa';
			const remains = remainingAttempts > 1 ? 'Restam' : 'Resta';

			notify.warn({
				title: 'Atenção',
				description: (
					<>
						{remains} apenas{' '}
						<Typography.Label.M>
							{remainingAttempts} {tries} antes do bloqueio temporário
						</Typography.Label.M>{' '}
						da sua conta.
					</>
				),
				containerProps: { margin: NOTIFY_OFFSET_TOP },
			});
		}

		return { cause: 'invalidCredentials' };
	}

	if (response.status === 429) {
		const { 'x-login-retry-after': time } = response.headers;

		const cooldown = Math.ceil(Number(time) / 60);
		const minutes = cooldown > 1 ? 'minutos' : 'minuto';

		notify.negative({
			title: 'Excesso de tentativas',
			description: (
				<>
					Por motivo de segurança sua conta ficará bloqueada por{' '}
					<Typography.Label.M>
						{cooldown} {minutes}
					</Typography.Label.M>
					. Acesse com outra conta ou aguarde e tente novamente.
				</>
			),
			containerProps: { margin: NOTIFY_OFFSET_TOP },
		});

		return { cause: 'blocked' };
	}

	return { cause: undefined };
}

export const OTPServices = {
	common: {
		signup: {
			get: getSignupOTP,
			validate: validateSignupOPT,
		},

		verification: {
			get: getVerificationOTP,
			validate: validateVerificationOTP,
		},
	},

	partner: {
		signup: {
			get: getPartnerSignupOTP,
			validate: validatePartnerSignupOTP,
		},

		verification: {
			get: getPartnerVerificationOTP,
			validate: validatePartnerVerificationOTP,
		},
	},
} as const;

//* Signup OTP Types
export type GetSignupOTPTypes = typeof OTPServices.common.signup.get | typeof OTPServices.partner.signup.get;

export type GetSignupOTPRequests = Parameters<GetSignupOTPTypes>[0];
export type GetSignupOTPResponses = Awaited<ReturnType<GetSignupOTPTypes>>;

export type ValidateSignupOTPTypes =
	| typeof OTPServices.common.signup.validate
	| typeof OTPServices.partner.signup.validate;
export type ValidateSignupOTPRequests = Parameters<ValidateSignupOTPTypes>[0];
export type ValidateSignupOTPResponses = Awaited<ReturnType<ValidateSignupOTPTypes>>;

//* Verification OTP Types

export type GetVerificationOTPTypes =
	| typeof OTPServices.common.verification.get
	| typeof OTPServices.partner.verification.get;
export type GetVerificationOTPRequests = Parameters<GetVerificationOTPTypes>[0];
export type GetVerificationOTPResponses = Awaited<ReturnType<GetVerificationOTPTypes>>;

export type ValidateVerificationOTPTypes =
	| typeof OTPServices.common.verification.validate
	| typeof OTPServices.partner.verification.validate;
export type ValidateVerificationOTPRequests = Parameters<ValidateVerificationOTPTypes>[0];
export type ValidateVerificationOTPResponses = Awaited<ReturnType<ValidateVerificationOTPTypes>>;
